import { useEffect, useRef } from 'react'
import { fetchRecentFlightSearches } from 'actions/FlightActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { getLocalRecentFlightSearches } from 'lib/flights/flightUtils'

export const RECENT_SEARCHES_STATUS = {
  FETCHING: 'fetching',
  AVAILABLE: 'available',
  ERROR: 'error',
  DISABLED: 'disabled',
  INITIAL: 'initial',
} as const

export type RecentSearchesStatus = (typeof RECENT_SEARCHES_STATUS)[keyof typeof RECENT_SEARCHES_STATUS]

export type RecentFlightSearchesData = {
  data: Array<App.RecentFlightSearch> | undefined
  status: RecentSearchesStatus
  shouldShowRecentSearches: boolean
}

function useRecentFlightSearches(): RecentFlightSearchesData {
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))
  const disableApi = !isLoggedIn

  const dispatch = useAppDispatch()
  const recentFlightSearches = useAppSelector(state => state.flights.recentFlightSearches)

  const hasFetched = useRef(false)

  useEffect(() => {
    if (disableApi) return
    if (!hasFetched.current && !recentFlightSearches?.fetching) {
      dispatch(fetchRecentFlightSearches())
      hasFetched.current = true
    }
  }, [disableApi, dispatch, recentFlightSearches.fetching])

  let status: RecentSearchesStatus = RECENT_SEARCHES_STATUS.INITIAL
  let data: Array<App.RecentFlightSearch> | undefined = recentFlightSearches?.data

  if (disableApi) {
    const localData = getLocalRecentFlightSearches()
    status = Array.isArray(localData) ? RECENT_SEARCHES_STATUS.AVAILABLE : RECENT_SEARCHES_STATUS.DISABLED
    data = localData
  } else if (recentFlightSearches?.error) {
    status = RECENT_SEARCHES_STATUS.ERROR
  } else if (recentFlightSearches?.fetching) {
    status = RECENT_SEARCHES_STATUS.FETCHING
  } else if (recentFlightSearches?.data) {
    if (Array.isArray(recentFlightSearches?.data)) {
      status = RECENT_SEARCHES_STATUS.AVAILABLE
    } else {
      status = RECENT_SEARCHES_STATUS.ERROR
    }
  }

  const shouldShowRecentSearches =
    (status === RECENT_SEARCHES_STATUS.AVAILABLE && data && data.length > 0) ||
    status === RECENT_SEARCHES_STATUS.FETCHING

  return {
    data,
    status,
    shouldShowRecentSearches,
  }
}

export default useRecentFlightSearches
